import { registerInitialState } from '@modules/authentication/constants';
import { SignInWithSSO } from '@modules/signInWithSSO/component';
import { SignInWithSSOButton } from '@modules/signInWithSSO/component/SignInWithSSOButton';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AuthService from 'services/AuthService';
import ErrorCode from 'v2/core/errors/codes';
import useListenAuth from 'v2/core/services/auth/useListenAuth';
import { RegisterState } from 'v2/views/layouts/authenticationModule/authTypes';
import { AUTH_ERROR_CODE_MESSAGE } from 'v2/views/layouts/authenticationModule/constants';
import { AuthenticationCardWrapper } from '../AuthenticationCardWrapper';
import { RegisterWithVerificationView } from '../RegisterWithVerificationView';
import { SignInOrRegisterContainer } from '../SignInOrRegisterContainer';
import { AuthView } from '../SignInOrRegisterContainer/types';
import { SignInWithGoogle } from './SignInWithGoogle';
import { SignUpWithGoogle } from './SignUpWithGoogle';

interface AuthenticationModuleProps {
  onLogin: () => void;
}

export const AuthenticationModule = ({
  onLogin,
}: AuthenticationModuleProps) => {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');
  const authError = searchParams.get('auth_error');
  const errorDescription = searchParams.get('error_description');

  const [isSignInWithSSOVisible, setIsSignInWithSSOVisible] = useState(
    view === 'sso',
  );
  const [isOTPViewVisible, setIsOTPViewVisible] = useState(false);
  const [userPayload, setUserPayload] =
    useState<RegisterState>(registerInitialState);
  const [selectedAuthView, setSelectedAuthView] = useState<AuthView>(
    view === AuthView.Register ? AuthView.Register : AuthView.SignIn,
  );
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const showUnapprovedUserError = AuthService.getIsUnapprovedUser();
  const showSuspendedUserError = AuthService.getIsSuspendedUser();
  const showInviteCodeVerificationError =
    AuthService.getIsInviteCodeVerificationFailed();
  const showInvalidSsoIdpTokenError =
    AuthService.getIsInvalidSsoIdpTokenViaGoogle();
  const showNoAccessToClientApplicationError =
    AuthService.getIsNoAccessToClientApplication();
  const showTrialExpiredError = AuthService.getIsTrialExpired();
  const showCustomAuthErrorMessage = AuthService.getCustomAuthErrorMessage();

  const params = useParams<{ type: string }>();

  const { type: loadingState } = params;

  const getErrorMessage = useCallback(() => {
    if (showInviteCodeVerificationError) {
      return 'Invitation link is invalid or expired. Please reach out to your organization admins for a new link.';
    }
    if (showUnapprovedUserError) {
      return AUTH_ERROR_CODE_MESSAGE[ErrorCode.UserUnapproved];
    }
    if (showSuspendedUserError) {
      return AUTH_ERROR_CODE_MESSAGE[ErrorCode.UserSuspended];
    }
    if (showInvalidSsoIdpTokenError) {
      return AUTH_ERROR_CODE_MESSAGE[ErrorCode.InvalidSsoIdpToken];
    }
    if (showTrialExpiredError) {
      return AUTH_ERROR_CODE_MESSAGE[ErrorCode.TrialExpired];
    }
    if (showNoAccessToClientApplicationError) {
      return "You don't have access to the Enterpret app on your SSO provider. Please contact your org's SSO admin.";
    }
    if (showCustomAuthErrorMessage) {
      return showCustomAuthErrorMessage;
    }
    if (authErrorMessage) {
      return authErrorMessage;
    }
  }, [
    showInviteCodeVerificationError,
    showUnapprovedUserError,
    showSuspendedUserError,
    showInvalidSsoIdpTokenError,
    showTrialExpiredError,
    showNoAccessToClientApplicationError,
    showCustomAuthErrorMessage,
    authErrorMessage,
  ]);

  const errorMessage = getErrorMessage();

  const { password, ...edgeServerPayload } = userPayload;

  const listenHub = useListenAuth({
    edgeServerPayload,
    onLogin: onLogin,
    setErrorMessage: setAuthErrorMessage,
  });

  // TODO: Verify if this works the same as in v1 (useMount hook call)
  useEffect(() => {
    const onBeforeUnloadEvent = () => {
      AuthService.removeIsInvalidSsoIdpTokenViaGoogle();
      AuthService.removeIsUnapprovedUser();
      AuthService.removeIsSuspendedUser();
      AuthService.removeIsInviteCodeVerificationFailed();
      AuthService.removeIsNoAccessToClientApplication();
      AuthService.removeCustomAuthErrorMessage();
      AuthService.removeIsTrialExpired();
    };
    window.addEventListener('beforeunload', onBeforeUnloadEvent);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnloadEvent);
    };
  }, []);

  useEffect(() => {
    listenHub();
  }, [userPayload, listenHub]);

  useEffect(() => {
    if (!authError && !errorDescription) {
      return;
    }

    if (
      authError === 'access_denied' &&
      errorDescription
        ?.toLowerCase()
        ?.includes('user is not assigned to the client application')
    ) {
      window.location.href = '/logout';
      AuthService.setIsNoAccessToClientApplication();
      return;
    }

    if (errorDescription) {
      window.location.href = '/logout';
      AuthService.setCustomAuthErrorMessage(errorDescription);
      return;
    }

    window.location.href = '/logout'; //  if unknown auth error
    AuthService.setCustomAuthErrorMessage(
      'Something went wrong while logging in. Please try again.',
    );
  }, [authError, errorDescription]);

  if (loadingState === 'loading') {
    return (
      <Stack
        height="100vh"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body1">Please wait. Logging you in...</Typography>
      </Stack>
    );
  }

  if (isSignInWithSSOVisible || showInvalidSsoIdpTokenError) {
    return (
      <SignInWithSSO
        onBackClick={() => setIsSignInWithSSOVisible(false)}
        shouldHideBackButton={showInvalidSsoIdpTokenError}
      />
    );
  }

  if (isOTPViewVisible) {
    return (
      <AuthenticationCardWrapper onGoBack={() => setIsOTPViewVisible(false)}>
        <RegisterWithVerificationView userPayload={userPayload} />
      </AuthenticationCardWrapper>
    );
  }

  const isRegisterView = selectedAuthView === AuthView.Register;

  return (
    <AuthenticationCardWrapper>
      <Stack spacing={2} mb={2}>
        {isRegisterView ? <SignUpWithGoogle /> : <SignInWithGoogle />}
        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}
        <Divider />
      </Stack>
      <SignInOrRegisterContainer
        selectedView={selectedAuthView}
        setUserPayload={setUserPayload}
        setAuthErrorMessage={setAuthErrorMessage}
        setIsOTPViewVisible={setIsOTPViewVisible}
        setSelectedView={setSelectedAuthView}
      />
      <SignInWithSSOButton onClick={() => setIsSignInWithSSOVisible(true)} />
    </AuthenticationCardWrapper>
  );
};
